import { useChannelsAvailability } from '@vp/contact-channels'
import type { ComponentType, ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ContactChannel } from '../components/page'
import { STEP_REGISTRY } from '../config/step-registry'
import { resolveChannelStatus } from '../utils/channel-status.utils'

export interface NextConfig {
  label?: string
  disabled?: boolean
}

export interface PageFlowContextValue {
  currentStepIndex: number
  totalSteps: number
  visibleStepCount: number
  isConfirmationStep: boolean
  channel: ContactChannel
  StepComponent: ComponentType
  goNext: () => void
  goBack: () => void
  showBack: boolean
  showFooter: boolean
  setFooterVisible: (visible: boolean) => void
  nextConfig: NextConfig
  setNextConfig: (config: NextConfig) => void
  registerOnNext: (handler: (() => void | Promise<void>) | undefined) => void
  fireOnNext: () => void
  nextLoading: boolean
  setNextLoading: (loading: boolean) => void
  channelDisabled: boolean
  channelStatusAlertKey: string | null
}

const INITIAL_NEXT_CONFIG: NextConfig = {
  disabled: true,
}

const PageFlowContext = createContext<PageFlowContextValue | null>(null)

interface PageFlowProviderProps {
  channel: ContactChannel
  children: ReactNode
}

export const PageFlowProvider = ({ channel, children }: PageFlowProviderProps) => {
  const steps = STEP_REGISTRY[channel]
  const [currentStepIndex, setCurrentStepIndex] = useState(0)
  const [footerVisible, setFooterVisibleState] = useState(true)
  const [nextConfig, setNextConfigState] = useState<NextConfig>(INITIAL_NEXT_CONFIG)
  const [nextLoading, setNextLoadingState] = useState(false)

  const { channelInfo, isLoading } = useChannelsAvailability(['callMe', 'email'])
  const { alertKey: channelStatusAlertKey, channelDisabled } = resolveChannelStatus(channel, channelInfo.callMe, channelInfo.email, isLoading)

  const onNextRef = useRef<(() => void | Promise<void>) | undefined>(undefined)

  const goNext = useCallback(() => {
    setNextConfigState(INITIAL_NEXT_CONFIG)
    setNextLoadingState(false)
    onNextRef.current = undefined
    setCurrentStepIndex((prev) => Math.min(steps.length - 1, prev + 1))
  }, [steps.length])

  const goBack = useCallback(() => {
    setNextConfigState(INITIAL_NEXT_CONFIG)
    setNextLoadingState(false)
    onNextRef.current = undefined
    setCurrentStepIndex((prev) => Math.max(0, prev - 1))
  }, [])

  const setNextConfig = useCallback((config: NextConfig) => {
    setNextConfigState((prev) => {
      if (prev.label === config.label && prev.disabled === config.disabled) {
        return prev
      }
      return config
    })
  }, [])

  const registerOnNext = useCallback((handler: (() => void | Promise<void>) | undefined) => {
    onNextRef.current = handler
  }, [])

  const fireOnNext = useCallback(() => {
    if (onNextRef.current) {
      onNextRef.current()
    } else {
      goNext()
    }
  }, [goNext])

  const setFooterVisible = useCallback((visible: boolean) => {
    setFooterVisibleState(visible)
  }, [])

  const setNextLoading = useCallback((loading: boolean) => {
    setNextLoadingState(loading)
  }, [])

  const StepComponent = (steps[currentStepIndex] ?? (() => null)) as ComponentType

  const value = useMemo<PageFlowContextValue>(
    () => ({
      currentStepIndex,
      totalSteps: steps.length,
      visibleStepCount: steps.length - 1,
      isConfirmationStep: currentStepIndex === steps.length - 1,
      channel,
      StepComponent,
      goNext,
      goBack,
      showBack: currentStepIndex > 0,
      showFooter: footerVisible,
      setFooterVisible,
      nextConfig,
      setNextConfig,
      registerOnNext,
      fireOnNext,
      nextLoading,
      setNextLoading,
      channelDisabled,
      channelStatusAlertKey,
    }),
    [
      currentStepIndex,
      steps.length,
      channel,
      StepComponent,
      goNext,
      goBack,
      footerVisible,
      setFooterVisible,
      nextConfig,
      setNextConfig,
      registerOnNext,
      fireOnNext,
      nextLoading,
      setNextLoading,
      channelDisabled,
      channelStatusAlertKey,
    ],
  )

  return <PageFlowContext.Provider value={value}>{children}</PageFlowContext.Provider>
}

export const usePageFlow = (): PageFlowContextValue => {
  const ctx = useContext(PageFlowContext)
  if (!ctx) {
    throw new Error('usePageFlow must be used inside <PageFlowProvider>')
  }
  return ctx
}
